import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { toISODate } from '../../../lib/datetime'
import type { AgendaListItem } from '../types'
import { businessMinuteNow, eventsOn } from '../week'
import DayColumn from './DayColumn'

interface Props {
  days: Date[]
  items: readonly AgendaListItem[]
  /** Kurumsal bugün (yerel gece yarısı) — vurgu ve "+" düğmesinin görünürlüğü buna göre. */
  today: Date
  loading: boolean
  /** Dar ekran: yedi gün dikey liste olarak (`CARD_VIEW_BREAKPOINT`). */
  stacked: boolean
  from: string
  onCreate: (isoDate: string) => void
  /**
   * Gün başlıklarındaki "+" düğmesi hiç çizilsin mi. Tarih koşulu (`day >= today`) bundan
   * AYRIDIR: bu, kipin yazmaya izin verip vermediğidir. Kısıtlı Yetkili asistan kipinde
   * {@code false} gelir (126 FR-019) — başlıktaki "Yeni" düğmesiyle aynı gerekçe, ama ORASI
   * ayrı bir yüzeydi ve ilk düzeltmede atlandı (push kapısı review'ü).
   */
  canCreateAny: boolean
}

/** Saat ızgarasının saat etiketleri (00:00 … 23:00). */
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

/** Hafta açıldığında görünüre kaydırılan saat — Outlook gibi mesai başı. */
const SCROLL_TO_HOUR = 8

/** "Şimdi" çizgisinin tazelenme aralığı. */
const NOW_TICK_MS = 60_000

/** Kurumsal saatin dakikası; dakikada bir tazelenir (yalnız ızgara görünümünde). */
function useBusinessMinute(enabled: boolean): number {
  const [minute, setMinute] = useState(() => businessMinuteNow())
  useEffect(() => {
    if (!enabled) return undefined
    const id = window.setInterval(() => setMinute(businessMinuteNow()), NOW_TICK_MS)
    return () => window.clearInterval(id)
  }, [enabled])
  return minute
}

/**
 * Haftanın yedi günü. Masaüstünde 24 saatlik ızgara (saat sütunu, "şimdi" çizgisi, açılışta mesai
 * başına kaydırma); dar ekranda (`stacked`) aynı günler alt alta düz liste.
 */
export default function WeekGrid({
  days,
  items,
  today,
  loading,
  stacked,
  from,
  onCreate,
  canCreateAny,
}: Readonly<Props>) {
  const todayIso = toISODate(today)
  const timeline = !stacked
  const nowMinute = useBusinessMinute(timeline)
  const scrollRef = useRef<HTMLDivElement>(null)
  const firstDay = days.length > 0 ? toISODate(days[0]) : ''

  // Hafta (ya da düzen) değişince mesai başına kaydır. Saat yüksekliği CSS'ten okunur — tek kaynak.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!timeline || !el) return
    const hour = Number.parseFloat(getComputedStyle(el).getPropertyValue('--agenda-hour'))
    if (Number.isFinite(hour)) el.scrollTop = hour * SCROLL_TO_HOUR
  }, [timeline, firstDay])

  const columns = days.map((day) => {
    const iso = toISODate(day)
    const isToday = iso === todayIso
    return (
      <DayColumn
        key={iso}
        day={day}
        events={eventsOn(items, day)}
        isToday={isToday}
        canCreate={day >= today && canCreateAny}
        loading={loading}
        from={from}
        onCreate={onCreate}
        timeline={timeline}
        nowMinute={timeline && isToday ? nowMinute : undefined}
      />
    )
  })

  if (stacked) return <div className="agenda-week agenda-week--stacked">{columns}</div>

  return (
    <div ref={scrollRef} className="agenda-week agenda-week--timeline">
      <div className="agenda-hours" aria-hidden="true">
        <div className="agenda-hours-head" />
        <div className="agenda-hours-body">
          {HOURS.map((label) => (
            <span key={label} className="agenda-hour-label">
              {label}
            </span>
          ))}
        </div>
      </div>
      {columns}
    </div>
  )
}
